// ADR-0009 — Captura de FOTO frontal para cadastro do template de liveness.
//
// Fluxo (browser, painel admin, membro presente):
//   idle → "Abrir câmera" (getUserMedia) → camera (preview ao vivo)
//        → "Capturar foto" (snapshot canvas → Blob) → captured (preview estático)
//        → "Cadastrar template" (POST multipart 'foto') → uploading → done | error
//
// Captura SÓ FOTO (snapshot único) — NÃO vídeo, NÃO face-api in-browser. A imagem
// vai como multipart 'foto' para o nosso Worker (POST /api/admin/users/:id/liveness-template),
// que proxia para /extract-template e cifra o template. A foto NÃO é armazenada.
// A câmera é desligada assim que a foto é capturada (e no unmount).

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@aions/ui-primitives';
import { apiFetch } from '../lib/api-client';

export interface FaceCaptureWidgetProps {
  readonly userId: string;
  readonly onEnrolled?: (reenrolled: boolean) => void;
}

type Phase = 'idle' | 'camera' | 'captured' | 'uploading' | 'done' | 'error';

export function FaceCaptureWidget({ userId, onEnrolled }: FaceCaptureWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reenrolled, setReenrolled] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const stopCamera = useCallback((): void => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  }, []);

  // Cleanup no unmount — desliga câmera + revoga o object URL do preview.
  useEffect(() => {
    return () => {
      stopCamera();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [stopCamera, previewUrl]);

  const openCamera = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // play() pode rejeitar em alguns ambientes; não é fatal para o preview.
        await videoRef.current.play().catch(() => undefined);
      }
      setPhase('camera');
    } catch {
      setError(t('members.liveness.error_camera'));
      setPhase('error');
    }
  }, [t]);

  const capture = useCallback((): void => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError(t('members.liveness.error_capture'));
      setPhase('error');
      return;
    }
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError(t('members.liveness.error_capture'));
          setPhase('error');
          return;
        }
        blobRef.current = blob;
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
        stopCamera();
        setPhase('captured');
      },
      'image/jpeg',
      0.92,
    );
  }, [t, previewUrl, stopCamera]);

  const retake = useCallback((): void => {
    blobRef.current = null;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setError(null);
    void openCamera();
  }, [previewUrl, openCamera]);

  const submit = useCallback(async (): Promise<void> => {
    if (!blobRef.current) return;
    setError(null);
    setPhase('uploading');
    const form = new FormData();
    form.append('foto', blobRef.current, 'cadastro.jpg');
    try {
      const resp = await apiFetch(`/api/admin/users/${userId}/liveness-template`, {
        method: 'POST',
        body: form,
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        const code = body.error ?? `http_${String(resp.status)}`;
        setError(
          t(`members.liveness.errors.${code}`, {
            defaultValue: t('members.liveness.errors.generic'),
          }),
        );
        setPhase('error');
        return;
      }
      const body = (await resp.json()) as { reenrolled?: boolean };
      setReenrolled(Boolean(body.reenrolled));
      setPhase('done');
      onEnrolled?.(Boolean(body.reenrolled));
    } catch {
      setError(t('members.liveness.errors.network'));
      setPhase('error');
    }
  }, [userId, t, onEnrolled]);

  return (
    <div className="card" data-testid="face-capture-widget" style={{ marginTop: 12 }}>
      <h3>{t('members.liveness.title')}</h3>
      <p className="cf-field__help">{t('members.liveness.subtitle')}</p>

      {error && (
        <Alert variant="error" data-testid="face-capture-error" style={{ marginTop: 12 }}>
          {error}
        </Alert>
      )}

      {/* Preview ao vivo (só visível durante a captura). */}
      <video
        ref={videoRef}
        playsInline
        muted
        data-testid="face-capture-video"
        style={{
          display: phase === 'camera' ? 'block' : 'none',
          width: '100%',
          maxWidth: 320,
          borderRadius: 8,
        }}
      />
      <canvas ref={canvasRef} style={{ display: 'none' }} data-testid="face-capture-canvas" />

      {/* Foto capturada (preview estático). */}
      {phase === 'captured' && previewUrl && (
        <img
          src={previewUrl}
          alt={t('members.liveness.preview_alt')}
          data-testid="face-capture-preview"
          style={{ width: '100%', maxWidth: 320, borderRadius: 8 }}
        />
      )}

      {phase === 'camera' && (
        <p className="cf-field__help" data-testid="face-capture-hint">
          {t('members.liveness.hint_camera')}
        </p>
      )}

      {phase === 'uploading' && (
        <div role="status" data-testid="face-capture-uploading">
          {t('members.liveness.uploading')}
        </div>
      )}

      {phase === 'done' && (
        <Alert
          variant="success"
          role="status"
          data-testid="face-capture-success"
          style={{ marginTop: 12 }}
        >
          {reenrolled ? t('members.liveness.success_reenrolled') : t('members.liveness.success')}
        </Alert>
      )}

      {/* Botões por fase. */}
      {(phase === 'idle' || phase === 'error') && (
        <Button onClick={() => void openCamera()} data-testid="face-capture-open">
          {t('members.liveness.open_camera')}
        </Button>
      )}
      {phase === 'camera' && (
        <Button onClick={capture} data-testid="face-capture-shoot">
          {t('members.liveness.capture')}
        </Button>
      )}
      {phase === 'captured' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" onClick={retake} data-testid="face-capture-retake">
            {t('members.liveness.retake')}
          </Button>
          <Button onClick={() => void submit()} data-testid="face-capture-submit">
            {t('members.liveness.submit')}
          </Button>
        </div>
      )}
    </div>
  );
}
